import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Note, InputCard, ResultCard, txt } from "./shared";
import { useLang } from "@/lib/i18n";

const diffYMD = (from, to) => {
  const end = new Date(to);
  end.setDate(end.getDate() + 1);
  let y = end.getFullYear() - from.getFullYear();
  let m = end.getMonth() - from.getMonth();
  let d = end.getDate() - from.getDate();
  if (d < 0) {
    m -= 1;
    d += 30;
  }
  if (m < 0) {
    y -= 1;
    m += 12;
  }
  return { y, m, d };
};

const normalize = (t) => {
  let { y, m, d } = t;
  m += Math.floor(d / 30);
  d %= 30;
  y += Math.floor(m / 12);
  m %= 12;
  return { y, m, d };
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const DateInput = ({ value, onChange, testid, label }) => (
  <div className="flex-1">
    <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-dim mb-2">{label}</label>
    <input
      type="date"
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-coal border border-line text-mist font-mono text-sm px-3 py-3 outline-none focus:border-gold transition-colors duration-300 [color-scheme:dark]"
    />
  </div>
);

export const TenureCalc = () => {
  const [periods, setPeriods] = useState([{ from: "2015-03-01", to: "2020-12-31" }, { from: "2021-01-15", to: "2026-09-30" }]);
  const { t: ui } = useLang();
  const s = ui.calc.tenure;

  const update = (i, key, val) => setPeriods((p) => p.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const remove = (i) => setPeriods((p) => p.filter((_, idx) => idx !== i));
  const add = () => setPeriods((p) => [...p, { from: "", to: "" }]);

  const valid = periods.filter((p) => p.from && p.to && new Date(p.from) <= new Date(p.to));
  const totals = valid.reduce(
    (acc, p) => {
      const r = diffYMD(new Date(p.from), new Date(p.to));
      return { y: acc.y + r.y, m: acc.m + r.m, d: acc.d + r.d };
    },
    { y: 0, m: 0, d: 0 }
  );
  const t = normalize(totals);
  const klass = (t.y * 0.6).toFixed(1).replace(".", ",");
  const py = plural(t.y, ...s.y);
  const pm = plural(t.m, ...s.m);
  const pd = plural(t.d, ...s.d);

  const rows = [
    { label: s.rows.periods, ...txt(String(valid.length)), testid: "tenure-row-periods" },
    { label: s.rows.years, ...txt(String(t.y)), testid: "tenure-row-years" },
    { label: s.rows.months, ...txt(String(t.m)), testid: "tenure-row-months" },
    { label: s.rows.days, ...txt(String(t.d)), testid: "tenure-row-days" },
    { label: s.rows.klass, ...txt(`${klass} %`), testid: "tenure-row-class" },
  ];
  const mainText = `${py}, ${pm}, ${pd}`;

  return (
    <>
      <InputCard kicker={s.kicker}>
        <div className="flex flex-col gap-5">
          {periods.map((p, i) => (
            <div key={i} data-testid={`tenure-period-${i}`} className="flex items-end gap-3">
              <DateInput label={s.from} value={p.from} onChange={(v) => update(i, "from", v)} testid={`tenure-from-${i}`} />
              <DateInput label={s.to} value={p.to} onChange={(v) => update(i, "to", v)} testid={`tenure-to-${i}`} />
              <button
                type="button"
                data-testid={`tenure-remove-${i}`}
                onClick={() => remove(i)}
                disabled={periods.length === 1}
                aria-label={s.remove}
                className="p-3 border border-line text-dim hover:text-gold hover:border-gold transition-colors duration-300 disabled:opacity-30 disabled:hover:text-dim disabled:hover:border-line"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="tenure-add-period"
          onClick={add}
          className="mt-6 inline-flex items-center gap-2 text-sm text-gold hover:text-gold-light transition-colors duration-300"
        >
          <Plus size={16} /> {s.add}
        </button>
        <Note>{s.note}</Note>
      </InputCard>
      <ResultCard
        title={s.title}
        service={s.service}
        note={s.note}
        label={s.result}
        mainText={mainText}
        main={
          <span className="text-4xl md:text-5xl leading-tight">
            {py}
            <span className="block text-2xl md:text-3xl text-gold/70 mt-2">
              {pm} · {pd}
            </span>
          </span>
        }
        rows={rows}
      />
    </>
  );
};
